import React, { useState } from 'react';
import EditIcon from '../../../assets/icons/edit.svg';
import PrimaryModal from '../Modal/PrimaryModal';
import Button from '../Button';
import { Editor } from '@tinymce/tinymce-react';
import { EditorPlugin, EditorToolBar, editorKey } from '../../../config/RichtextEditor';
import { decodeHtmlEntities } from '../../../utils/helper';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import InlineTooltip from '../Tooltip/InlineTooltip';

const TextArea = ({ value, id, label, handleChange, error, helpText, mandatory }) => {
    const [showEditor, setShowEditor] = useState(false);
    const [inputFieldValue, setInputFieldValue] = useState(value);
    const [description, setDescription] = useState(value);
    const { content } = useContentfulContext();

    const handleOpenModal = () => {
        setShowEditor(true);
    };

    const handleHideModal = () => {
        setShowEditor(false);
    };

    const handleSave = () => {
        const multiLineString = `${description}`;
        const singleLineString = multiLineString.replace(/\n/g, '');

        const updated = singleLineString.replaceAll(`<span class="mce-nbsp-wrap" contenteditable="false">&nbsp;</span>`, `&nbsp;`);
        const decoded = decodeHtmlEntities(updated);

        setInputFieldValue(decoded);
        setShowEditor(false);
        const target = { name: id, value: decoded };
        handleChange({ target });
    };

    return (
        <div className='mb-4' dzt-field-id={id}>
            {label && (
                <label
                    htmlFor={id}
                    className="block text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1.5 flex items-center justify-between"
                >
                    <span className="flex items-center gap-1.5">
                        {label} {mandatory && <span className="text-stride-danger">*</span>}
                        {helpText && (
                            <InlineTooltip
                                content={helpText}
                                imgClass="pt-0.5"
                            />
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={handleOpenModal}
                        className="text-xs font-medium text-stride-primary hover:text-stride-primary-hover flex items-center gap-1 transition-colors"
                    >
                        <img 
                            alt='Edit rich text'
                            src={EditIcon} 
                            className='h-3.5 w-3.5'
                        />
                        <span>Rich Editor</span>
                    </button>
                </label>
            )}
            <div className="relative">
                <textarea
                    disabled 
                    id={id}
                    name={id} 
                    rows={3}
                    className="w-full px-3.5 py-2.5 text-sm font-medium text-stride-heading bg-stride-bg rounded-lg border border-stride-border focus:outline-none transition-all placeholder:text-stride-muted disabled:bg-stride-bg/70 disabled:text-stride-body resize-none cursor-pointer" 
                    placeholder={label} 
                    value={inputFieldValue} 
                    onClick={handleOpenModal}
                />
            </div>

            {error && <p className="mt-1.5 text-xs text-stride-danger font-medium">{error}</p>}

            {showEditor && (
                <PrimaryModal title={label} hideModal={handleHideModal}>
                    <div className='p-6 space-y-5'>
                        <p className='text-sm text-stride-body'>
                            {content?.INPUT_TEXT_AREA_CONTENT?.header || "Edit formatted text in rich editor"}
                        </p>
                        <div className="rounded-lg border border-stride-border overflow-hidden">
                            <Editor
                                apiKey={editorKey}
                                onEditorChange={(e) => setDescription(e)}
                                value={description}
                                disabled={false}
                                init={{
                                    menubar: true,
                                    plugins: EditorPlugin,
                                    toolbar: EditorToolBar,
                                }}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-2.5 pt-2">
                            <Button variant="secondary" onClick={handleHideModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleSave}>
                                {content?.INPUT_TEXT_AREA_CONTENT?.save_btn || "Save"}
                            </Button>
                        </div>
                    </div>
                </PrimaryModal>
            )}
        </div>
    );
};

export default TextArea;
